import { Component, OnDestroy } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, Validators, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '@core/components/base/base.component';
import { ToastrService } from '@core/services/notification/toastr.service';
import { CustomerService } from '@core/services/customer/customer.service';
import { StorageService } from '@core/services/util/storage.service';
import { ChangePasswordComponent } from '../../change-password/change-password.component';
import { Customer } from '@shared/models/customer';
import { UtilityService } from '@app/core/services/util/utility.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ToggleInputDirective } from '@app/shared/directives/toggle-input.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PonchoUIModule } from '@poncho/ui';

@Component({
    selector: 'app-customer-info',
    templateUrl: './customer-info.component.html',
    styleUrls: ['./customer-info.component.scss'],
    standalone: true,
    imports: [NgTemplateOutlet, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, ToggleInputDirective, MatDatepickerModule, PonchoUIModule]
})

export class CustomerInfoComponent extends BaseComponent implements OnDestroy {

    customer: Customer;
    personalInfoForm: FormGroup;
    showSubmitButton: boolean = false;
    isFormUpdated: boolean = false;

    destroySubject$: Subject<void> = new Subject();
    showLoading: boolean = true;
    todayDate: Date = new Date();


    constructor(
        private customerService: CustomerService,
        private toastrService: ToastrService,
        private dialog: MatDialog,
        private storageService: StorageService,
        private datePipe: DatePipe,
        private utilityService: UtilityService,
    ) {
        super();
    }

    ngOnInitBrowser() {
        this.customerService.customer
            .pipe(takeUntil(this.destroySubject$))
            .subscribe(data => {
                this.customer = data;
                if (this.customer.customer_info) {
                    this.customer.birthday = this.customer.customer_info.birthday;
                }

                const phoneRegex = this.utilityService.phoneRegex;
                const emailRegex = this.utilityService.emailRegex;

                this.personalInfoForm = new FormGroup({
                    name: new FormControl({ value: this.customer.name, disabled: true }, Validators.compose([Validators.required])),
                    phone_no: new FormControl({ value: this.customer.phone_no, disabled: true }, Validators.compose([
                        Validators.required,
                        Validators.pattern(phoneRegex)
                    ])),
                    email: new FormControl({ value: this.customer.email, disabled: true }, Validators.compose([
                        Validators.required,
                        Validators.pattern(emailRegex)
                    ])),
                    birthday: new FormControl({ value: this.customer.birthday, disabled: true }, Validators.compose([
                        Validators.required
                    ])),
                    password: new FormControl({ value: '**********', disabled: true }, Validators.compose([
                        Validators.required
                    ])),
                });

                if (Object.keys(this.customer).length !== 0) {
                    this.showLoading = false;
                }

                this._listenFormChangeEvent();
            });

        this.customerService.getCustomer();
    }

    get f() {
        return this.personalInfoForm.controls;
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    private _listenFormChangeEvent() {
        this.personalInfoForm.valueChanges
            .subscribe(() => {

                let dirtyValues = {};

                Object.keys(this.personalInfoForm.controls)
                    .every(key => {
                        let currentControl = this.personalInfoForm.controls[key];

                        if (currentControl.dirty) {
                            if(this.customer[key] !== String(currentControl.value)) {
                                dirtyValues[key] = currentControl.value;
                                return false;
                            }
                        }
                        return true;
                    });

                if (Object.entries(dirtyValues).length !== 0) {
                    this.showSubmitButton = true;
                } else {
                    this.showSubmitButton = false;
                }
            });
    }

    showUpdatePasswordModal(): void {
        this.dialog.open(ChangePasswordComponent, { maxWidth: 500 });
    }

    updateCustomer(form: any) {
        if(this.personalInfoForm.invalid) {
            return;
        }
        const name = form.name ? form.name : this.customer.name;
        const email = form.email ? form.email : this.customer.email;
        let birthdayDate = form.birthday ? form.birthday : this.customer.birthday;
        if(birthdayDate) {
            birthdayDate = this.datePipe.transform(birthdayDate, 'yyyy-MM-dd');
        }

        this.customerService.updatePersonalInfo(name, email, birthdayDate)
            .subscribe((data: any) => {
                if (data && data.meta) {
                    if (data.meta.code === 200) {
                        this.toastrService.openSnackBar('Updated Successfully', 'OKAY');
                        this.personalInfoForm.disable();
                        this.showSubmitButton = false;
                        this.isFormUpdated = true;
                    } else {
                        this.toastrService.openSnackBar(data.meta.message, 'OKAY');
                    }
                }
            }, (err) => {
                console.log('updateCustomer:updatePersonalInfo', err);
                this.toastrService.openSnackBar("Something went wrong! Please try again.", "OKAY");
            });
    }

    isLoggedIn() {
        if (this.storageService.get('session-id') && this.storageService.get('phone_no')) {
            return true;
        }
        return false;
    }
}

